import { formatDateLong, formatTime } from '@common/utils/date';
import { listify } from '@common/utils/listify';
import {
	EVENT_DESCRIPTION_DELIMITER,
	PRODUCTION_WEBSITE_URL,
} from '@data/constants';
import { SupabaseEventRecord } from '@interfaces';

export const getTicketUrl = (
	event: SupabaseEventRecord,
	accessCodeValue?: string,
) => {
	const ticketUrl = new URL(event.ticket_url);

	if (accessCodeValue) {
		ticketUrl.searchParams.set('discount', accessCodeValue);
	}

	return ticketUrl.href;
};

type GetEventDescriptionParams = {
	event: SupabaseEventRecord;
	hideAgeRestriction?: boolean;
	hidePresentedBy?: boolean;
};

export const getEventDescription = ({
	event,
	hideAgeRestriction = false,
	hidePresentedBy = false,
}: GetEventDescriptionParams): string | undefined => {
	const eventDescription = [];

	if (event.description) {
		const hasDelimiter = event.description.includes(
			EVENT_DESCRIPTION_DELIMITER,
		);

		eventDescription.push(
			hasDelimiter
				? `${event.description.split(EVENT_DESCRIPTION_DELIMITER)[0]}\n\n`
				: null,
		);
	}

	if (event.age_restriction && !hideAgeRestriction) {
		eventDescription.push(`${event.age_restriction}\n`);
	}

	if (event.presented_by && !hidePresentedBy) {
		eventDescription.push(event.presented_by);
	}

	return eventDescription.filter((part) => !!part).join('');
};

// Gather some data to build an event description out of
export const getEventMetaDescription = (event: SupabaseEventRecord) => {
	const listifiedVenues = listify(event.venues);
	const date = formatDateLong(event.start_date);
	const earliestTime = formatTime(event.door_time ?? event.start_date);

	// First artist is headliner (use event name as fallback)
	const artist = event.artists[0] || event.name;

	return `${artist} performing on ${date} at ${listifiedVenues} from ${earliestTime} at Elsewhere, Brooklyn NY`;
};

interface GetEventUrlParams {
	event: SupabaseEventRecord;
}

// Get the url of the event on the Elsewhere website
export function getEventUrl({ event }: GetEventUrlParams) {
	const url = new URL(PRODUCTION_WEBSITE_URL);

	if (event.elsewhere_presents) {
		url.pathname = `/presents/${event.slug}`;
	} else {
		url.pathname = `/events/${event.slug}`;
	}

	return url;
}
